import type { ReactNode } from 'react';

import { cn } from '@platform/ui/lib/utils';

/**
 * What a list or a tab shows when it holds nothing (docs/11 §4). Written as
 * guidance rather than an apology: an empty catalogue is a seller's first step,
 * not a failure, so the state carries the action that fills it.
 */
export function EmptyState({
    title,
    description,
    action,
    icon,
    titleAs: Title = 'p',
    className,
}: {
    title: ReactNode;
    description?: ReactNode;
    action?: ReactNode;
    /** A 24px Lucide icon naming what is missing. */
    icon?: ReactNode;
    /**
     * The title's element: a paragraph inside a card or a list, the page's
     * heading when the state is the whole page (docs/11 §4).
     */
    titleAs?: 'p' | 'h1' | 'h2';
    className?: string;
}) {
    return (
        <div data-slot="empty-state" className={cn('flex flex-col items-center gap-2 px-6 py-10 text-center', className)}>
            {icon && <div className="mb-2 text-subtle-foreground [&_svg]:size-6">{icon}</div>}
            <Title className="text-16 font-medium text-foreground">{title}</Title>
            {description && <p className="max-w-lg text-14 text-muted-foreground">{description}</p>}
            {action && <div className="mt-2 flex flex-wrap justify-center gap-2">{action}</div>}
        </div>
    );
}
